import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Code2,
  Layers,
  Cpu,
  Bot,
  ShieldCheck,
  GraduationCap,
  Wrench,
  Globe2,
  Sparkles,
  Building2,
  MapPin,
  CheckCircle2,
  Award,
  ArrowRight,
  Shield,
  FileCheck,
  Activity,
  Compass,
  Target,
  Check,
} from "lucide-react";
import { PageHero } from "../components/PageHero";
import { scrollToElement } from "../hooks/useRoute";

const acronymLetters = [
  {
    letter: "R",
    word: "Research",
    desc: "Applied scientific inquiry & algorithmic architectures",
  },
  {
    letter: "A",
    word: "And",
    desc: "Integrated multidisciplinary execution",
  },
  {
    letter: "D",
    word: "Development",
    desc: "Scalable software & product engineering",
  },
  {
    letter: "I",
    word: "Infrastructure",
    desc: "Robust networks, cloud backends & IoT telemetry",
  },
  {
    letter: "E",
    word: "Electronic",
    desc: "Embedded circuits, IoT sensors, PCB design & microcontrollers",
  },
  {
    letter: "C",
    word: "Computing",
    desc: "High-performance platforms & RaBuOS",
  },
  {
    letter: "E",
    word: "Equipment",
    desc: "Robotics, automation & hardware lab systems",
  },
];

const servicePillars = [
  {
    title: "IT & ITeS Services",
    text: "Enterprise software development, technical consultancy, systems architecture, networking, and digital infrastructure.",
    icon: Code2,
    badge: "Core Software",
  },
  {
    title: "SaaS & Digital Platforms",
    text: "Multi-tenant cloud architectures, RaBuOS business operating platform, dynamic CMS applications, and managed hosting.",
    icon: Layers,
    badge: "Cloud & SaaS",
  },
  {
    title: "Research & Development",
    text: "Applied research, hardware prototyping, IoT sensor telemetry, microcontroller firmware, and specialized testing facilities.",
    icon: Cpu,
    badge: "R&D & IoT Lab",
  },
  {
    title: "Robotics & Automation",
    text: "Industrial IoT (IIoT), motion control, PLC/SCADA telemetry, and preventative maintenance for automated workflows.",
    icon: Bot,
    badge: "Robotics & Hardware",
  },
  {
    title: "Security Systems",
    text: "Connected IoT surveillance networks, biometric access control, perimeter intrusion alarms, network firewall hardening, and security audits.",
    icon: ShieldCheck,
    badge: "Cyber & Physical",
  },
  {
    title: "Training & Education",
    text: "Corporate developer upskilling, institutional lab setup, and structured engineering tracks across Full-Stack, IoT & Embedded systems.",
    icon: GraduationCap,
    badge: "Education Core",
  },
];

const foundingPrinciples = [
  {
    title: "Engineer-led",
    desc: "Ground every solution in testing, measurement, clear engineering rationale, and rigorous practical validation.",
    icon: Wrench,
    accent: "text-sky-500",
    bg: "bg-sky-500/10 border-sky-500/20",
  },
  {
    title: "Technology for All",
    desc: "Make capable technology practical, understandable, and accessible to organisations and individuals regardless of background.",
    icon: Globe2,
    accent: "text-emerald-500",
    bg: "bg-emerald-500/10 border-emerald-500/20",
  },
  {
    title: "Education is Freedom",
    desc: "Treat learning not as a compliance requirement but as a direct pathway to self-reliance, capability, and long-term opportunity.",
    icon: Sparkles,
    accent: "text-amber-500",
    bg: "bg-amber-500/10 border-amber-500/20",
  },
];

const companyHighlights = [
  {
    id: "statutory",
    icon: Building2,
    title: "Corporate Identity",
    desc: "RADIECE PRIVATE LIMITED • CIN: U58200KL2026PTC104862 • GSTIN: 32AAQCR1162B1ZR",
  },
  {
    id: "registered-office",
    icon: MapPin,
    title: "Operational Base",
    desc: "Headquartered at CSEZ P.O, Kakkanad, Ernakulam, Kerala.",
  },
  {
    id: "compliance",
    icon: Shield,
    title: "Statutory Compliance",
    desc: "DPDP Act 2023, CERT-In aligned, and standard AMC guarantees.",
  },
  {
    id: "standards",
    icon: Award,
    title: "Engineering Quality",
    desc: "ISO/IEEE architecture standards and validated deployment workflows.",
  },
];

export function About({ scrollTo }: { scrollTo?: string }) {
  useEffect(() => {
    if (scrollTo) {
      scrollToElement(scrollTo);
    }
  }, [scrollTo]);

  return (
    <>
      <PageHero
        badge="Corporate Profile & Vision"
        title="About RADIECE PRIVATE LIMITED"
        subtitle="Intelligence, Engineered."
        description="RADIECE PRIVATE LIMITED delivers technology services across software platforms, systems integration, hardware R&D, robotics, and intelligent engineering."
        maxWidth="max-w-5xl"
      >
        <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-slate-200">
            <Building2 className="w-3.5 h-3.5 text-primary" />
            MCA Registered Entity
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-slate-200">
            <MapPin className="w-3.5 h-3.5 text-emerald-400" />
            CSEZ Kakkanad, Ernakulam
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-slate-200">
            <Shield className="w-3.5 h-3.5 text-sky-400" />
            DPDP & CERT-In Compliant
          </span>
        </div>
      </PageHero>

      <section className="no-print bg-slate-50 dark:bg-[#070b12] py-16 sm:py-20 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {/* Who We Are & Company Overview */}
          <div id="who-we-are" className="scroll-mt-28 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 dark:bg-primary/10 border border-brand/20 dark:border-primary/20 text-brand dark:text-primary text-xs font-bold uppercase tracking-wider mb-3">
                <Compass className="w-3.5 h-3.5 text-brand dark:text-primary" />
                Who We Are
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight mb-6 leading-tight">
                A technology company operating with clarity, capability, and purpose.
              </h2>
              <div className="space-y-4 text-base text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                <p>
                  <strong className="font-semibold text-slate-900 dark:text-white">RADIECE PRIVATE LIMITED</strong> is an engineering practice that builds technology solutions across enterprise software, multi-tenant digital platforms, embedded electronics, Industrial IoT (IIoT), robotics, and intelligent systems.
                </p>
                <p>
                  From our headquarters in <strong className="font-semibold text-slate-900 dark:text-white">Kakkanad, Ernakulam</strong>, we engineer dependable digital and physical systems for clients across IT & ITeS, SaaS, Industrial IoT, corporate engineering upskilling, surveillance security, and electronic hardware prototyping.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white dark:bg-[#0e1724] border border-slate-200/80 dark:border-slate-800 shadow-xs transition-colors">
                  <span className="text-2xl sm:text-3xl font-extrabold text-brand dark:text-primary block font-mono">6</span>
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Practice Verticals</span>
                </div>
                <div className="p-4 rounded-xl bg-white dark:bg-[#0e1724] border border-slate-200/80 dark:border-slate-800 shadow-xs transition-colors">
                  <span className="text-2xl sm:text-3xl font-extrabold text-brand dark:text-primary block font-mono">100%</span>
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Statutory Transparency</span>
                </div>
              </div>
            </motion.div>

            {/* Structured Highlights Grid */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {companyHighlights.map((h, idx) => (
                <motion.div
                  key={h.title}
                  id={h.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="scroll-mt-28 bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-6 shadow-xs flex flex-col justify-between hover:border-brand/40 dark:hover:border-primary/40 hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 text-brand dark:text-primary border border-slate-200/80 dark:border-slate-700 flex items-center justify-center mb-4 group-hover:bg-primary/10 group-hover:text-brand dark:group-hover:text-primary group-hover:scale-105 transition-all">
                    <h.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-brand-dark dark:text-slate-100 mb-1 group-hover:text-brand dark:group-hover:text-primary transition-colors">
                      {h.title}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-light">
                      {h.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Meaning Behind RADIECE - Full-Width Interactive Animated Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-gradient-to-br from-[#0d1723] via-brand-dark to-[#101c2a] rounded-3xl p-8 sm:p-12 text-white border border-white/10 shadow-xl overflow-hidden w-full"
          >
            {/* Ambient Lighting Glow */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.12, 0.22, 0.12],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -right-20 -top-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none"
              aria-hidden="true"
            />

            <div className="relative z-10 w-full">
              <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs font-bold uppercase tracking-wider mb-4">
                <Cpu className="w-3.5 h-3.5" />
                Origin of Our Identity
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4 leading-snug">
                The Meaning Behind RADIECE
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-light mb-8">
                <strong className="text-primary font-semibold">RADIECE</strong> is an acronym representing:
              </p>

              {/* Clean Legible Acronym Full-Width Banner */}
              <div className="p-6 sm:p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm shadow-inner w-full">
                <div className="flex flex-wrap items-baseline justify-start sm:justify-between gap-x-3 gap-y-2 text-base sm:text-xl lg:text-2xl font-medium text-slate-100 leading-relaxed w-full">
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">R</span>esearch
                  </span>
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">A</span>nd
                  </span>
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">D</span>evelopment
                  </span>
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">I</span>nfrastructure
                  </span>
                  <span className="text-slate-400 font-normal text-sm sm:text-lg">for</span>
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">E</span>lectronic
                  </span>
                  <span className="text-slate-400 font-normal text-sm sm:text-lg">and</span>
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">C</span>omputing
                  </span>
                  <span>
                    <span className="text-primary font-extrabold text-lg sm:text-2xl lg:text-3xl">E</span>quipment
                  </span>
                </div>
              </div>

              {/* Interactive Full-Width 7-Letter Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 mt-6 w-full">
                {acronymLetters.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9, y: 15 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -5, scale: 1.04, transition: { duration: 0.2 } }}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary/50 rounded-2xl p-4 sm:p-5 flex flex-col justify-between transition-colors group cursor-default shadow-xs w-full"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 text-primary flex items-center justify-center font-extrabold text-lg font-mono mb-3 group-hover:bg-primary group-hover:text-slate-950 transition-all shadow-xs">
                      {item.letter}
                    </div>
                    <div>
                      <span className="text-xs sm:text-sm font-bold text-white block group-hover:text-primary transition-colors">
                        {item.word}
                      </span>
                      <span className="text-[10px] sm:text-[11px] text-slate-400 leading-tight block mt-1.5 font-light">
                        {item.desc}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              <p className="text-xs sm:text-sm text-slate-400 mt-6 leading-relaxed font-light w-full">
                This foundation reflects our engineering integration: connecting custom electronic hardware prototyping with high-scale enterprise cloud software architectures.
              </p>
            </div>
          </motion.div>

          {/* Vision & Mission - Dual Interactive Cards */}
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45 }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="card-interactive bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-8 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-950/60 text-brand dark:text-sky-300 flex items-center justify-center mb-5 border border-sky-100 dark:border-sky-850">
                  <Compass className="w-6 h-6 text-brand dark:text-sky-300" />
                </div>
                <p className="text-brand dark:text-primary font-bold text-xs uppercase tracking-wider mb-2">Our Vision</p>
                <h3 className="text-2xl font-bold text-brand-dark dark:text-slate-100 mb-4 tracking-tight">
                  Accessible & Trustworthy Technology
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                  To make trustworthy technology and lifelong learning accessible to all — enabling people and organisations to participate confidently in a secure, capable, and inclusive digital future.
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center text-xs font-bold text-brand dark:text-primary uppercase tracking-wider">
                Inclusive • Trustworthy • Future-Proof
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: 0.08 }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="card-interactive bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-8 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 flex items-center justify-center mb-5 border border-emerald-100 dark:border-emerald-850">
                  <Target className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                </div>
                <p className="text-emerald-700 dark:text-emerald-400 font-bold text-xs uppercase tracking-wider mb-2">Our Mission</p>
                <h3 className="text-2xl font-bold text-brand-dark dark:text-slate-100 mb-4 tracking-tight">
                  Engineering-Led Execution
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                  To research, engineer, and deliver dependable software, electronics, robotics, security, and learning solutions that are practical, responsible, and accessible for businesses and individuals.
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                Measurement • Verification • Quality
              </div>
            </motion.div>
          </div>

          {/* Founding Principles with Visual Accents */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Founding Principles</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
                Grounded in engineering discipline and responsibility.
              </h2>
              <p className="mt-3 text-slate-500 dark:text-slate-400 text-sm sm:text-base font-light">
                The core values that govern how we build software, design electronic hardware, and treat every client partnership.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {foundingPrinciples.map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  className="card-interactive bg-white dark:bg-[#0e1724] rounded-2xl p-7 shadow-xs border border-slate-200/90 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 flex flex-col justify-between transition-colors"
                >
                  <div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 border ${item.bg} ${item.accent}`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-dark dark:text-slate-100 mb-3">{item.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-light">{item.desc}</p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center gap-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    <span>Active Company Standard</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 6 Core Service Pillars with Rich Icon Badges */}
          <div id="service-pillars" className="scroll-mt-28">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Practice Areas</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
                6 Service Pillars Built on Deep Capability
              </h2>
              <p className="mt-3 text-slate-500 dark:text-slate-400 text-sm sm:text-base font-light">
                Discover the comprehensive scope of technology, systems, and engineering practice areas we deliver.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicePillars.map((pillar, idx) => (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: idx * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  className="card-interactive bg-white dark:bg-[#0e1724] rounded-2xl p-7 shadow-xs border border-slate-200/90 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 flex flex-col justify-between group transition-colors"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-5">
                      <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 text-brand dark:text-primary flex items-center justify-center border border-slate-200/80 dark:border-slate-700 group-hover:bg-primary/10 group-hover:text-brand dark:group-hover:text-primary group-hover:scale-105 transition-all">
                        <pillar.icon className="w-6 h-6" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full">
                        {pillar.badge}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2 group-hover:text-brand dark:group-hover:text-primary transition-colors">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                      {pillar.text}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs font-bold text-brand dark:text-primary group-hover:text-primary dark:group-hover:text-sky-300 transition-colors">
                    <span>Explore Vertical</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Delivery Mindset & System Assurance Guarantees */}
          <div className="grid gap-8 lg:grid-cols-2 items-stretch">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-8 shadow-xs flex flex-col justify-between transition-colors"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-brand dark:text-primary flex items-center justify-center mb-5 border border-primary/20">
                  <Activity className="w-6 h-6 text-brand dark:text-primary" />
                </div>
                <p className="text-brand dark:text-primary font-bold text-xs uppercase tracking-wider mb-2">Our Operating Method</p>
                <h3 className="text-2xl font-bold text-brand-dark dark:text-slate-100 mb-4 tracking-tight">
                  Engineering Discipline & Clarity
                </h3>
                <ul className="space-y-4 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                  {[
                    "We work with an engineering-led mindset, turning complex requirements into practical, well-structured digital and technical solutions.",
                    "Our delivery style is quality-focused and scope-conscious, prioritising clarity, precision, and dependable execution from concept to launch.",
                    "We approach each engagement as a long-term technology partnership, balancing innovation with disciplined delivery and transparent communication.",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-8 shadow-xs flex flex-col justify-between transition-colors"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 flex items-center justify-center mb-5 border border-emerald-100 dark:border-emerald-850">
                  <FileCheck className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                </div>
                <p className="text-emerald-700 dark:text-emerald-400 font-bold text-xs uppercase tracking-wider mb-2">Statutory Trust</p>
                <h3 className="text-2xl font-bold text-brand-dark dark:text-slate-100 mb-4 tracking-tight">
                  Quality & Maintenance Guarantees
                </h3>
                <ul className="space-y-4 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                  {[
                    "All custom web platforms and systems undergo rigorous pre-launch testing for performance, responsive rendering, and security.",
                    "We offer structured Annual Maintenance Contracts (AMC) to provide clients with continuous technical support, backups, and security patching.",
                    "Our operations strictly comply with national standards including DPDP Act 2023 for data privacy and CERT-In cyber security guidelines.",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Call to Action */}
      <section className="no-print bg-slate-900 py-16 sm:py-20 text-white border-t border-slate-800">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
            Connect With RADIECE Leadership & Engineering
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base sm:text-lg mb-8 leading-relaxed font-light">
            Have questions about our company structure, corporate governance, engineering practices, or partnership opportunities?
          </p>
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            href="/contact"
            className="btn-shine inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-8 py-3.5 rounded-xl font-bold text-base transition-colors shadow-lg"
          >
            Go to Contact Page →
          </motion.a>
        </motion.div>
      </section>
    </>
  );
}
